import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TrainingCenterService } from './training-center.service';
import { UserService } from 'libs/user/user.service';
import { AuthorizationService } from 'libs/authorization/authorization.service';
import { User } from 'libs/user/user.model';

/**
 * S2-02 — protège /networks et /training-centers. Autorise sur l'un ou
 * l'autre des deux mécanismes :
 *  - l'historique TrainingCenter.isSuperAdmin du centre de l'identité ;
 *  - un droit admin réseau migré (décision 15, section 8 du plan) —
 *    auparavant un guard mort et jamais câblé dans
 *    libs/training-centers-network/, converge ici plutôt que d'exister en
 *    double.
 * Le périmètre précis (quels centres un admin réseau voit exactement) reste
 * un filtrage à faire dans les pages elles-mêmes au sprint 3 ; ce guard ne
 * décide que de l'accès à la zone, pas du contenu affiché dedans.
 */
@Injectable({
  providedIn: 'root',
})
export class SuperAdminGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private trainingCenterService: TrainingCenterService,
    private authorizationService: AuthorizationService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.userService.authGuardUser$.pipe(
      switchMap((user: User) => {
        const centreSuperAdmin$ = this.trainingCenterService
          .getSuperAdminGuardTrainingCenter(user.trainingCenterId)
          .pipe(map((centre) => !!centre && centre.isSuperAdmin === true));

        const reseauAdmin$ = this.authorizationService.hasAnyNetworkGrant(user.id);

        return combineLatest([centreSuperAdmin$, reseauAdmin$]).pipe(
          map(([centreSuperAdmin, reseauAdmin]) => centreSuperAdmin || reseauAdmin),
        );
      }),
    );
  }
}
